import React, { useEffect, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { CheckCircle2, Loader2, XCircle, ArrowRight } from "lucide-react";
import api from "../api";
import { useAuth } from "../context/AuthContext";

export default function CheckoutSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const { refreshUser } = useAuth();
  const [state, setState] = useState("checking"); // checking | success | error | timeout
  const attempts = useRef(0);

  useEffect(() => {
    let active = true;
    const poll = async () => {
      if (!sessionId) {
        setState("error");
        return;
      }
      if (attempts.current >= 6) {
        setState("timeout");
        return;
      }
      attempts.current += 1;
      try {
        const { data } = await api.get(`/payments/status/${sessionId}`);
        if (!active) return;
        if (data.payment_status === "paid") {
          await refreshUser();
          setState("success");
          return;
        }
        if (data.status === "expired") {
          setState("error");
          return;
        }
        setTimeout(poll, 2000);
      } catch {
        if (active) setTimeout(poll, 2000);
      }
    };
    poll();
    return () => {
      active = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionId]);

  return (
    <div className="min-h-[70vh] flex items-center justify-center px-5 py-16 animate-fade-in">
      <div className="w-full max-w-md bg-white rounded-[2rem] shadow-xl border border-amber/30 p-10 text-center">
        {state === "checking" && (
          <>
            <Loader2 size={54} className="text-plum mx-auto animate-spin" />
            <h1 className="font-serif text-3xl text-plum mt-5">Confirmando tu pago...</h1>
            <p className="font-body text-ink/60 mt-2">Un momento, estamos activando tu membresía.</p>
          </>
        )}
        {state === "success" && (
          <>
            <CheckCircle2 size={60} className="text-plum mx-auto" />
            <h1 className="font-serif text-4xl text-plum mt-5">¡Bienvenida!</h1>
            <p className="font-body text-ink/65 mt-3">
              Tu membresía está activa. Ya tienes acceso a cursos, foros y sesiones grupales.
            </p>
            <Link to="/cursos" className="inline-flex items-center gap-2 mt-7 bg-plum text-cream font-sans px-7 py-3.5 rounded-full hover:bg-[#684d89] transition-colors">
              Explorar cursos <ArrowRight size={18} />
            </Link>
          </>
        )}
        {(state === "error" || state === "timeout") && (
          <>
            <XCircle size={56} className="text-terracotta mx-auto" />
            <h1 className="font-serif text-3xl text-plum mt-5">
              {state === "timeout" ? "Estamos procesando tu pago" : "No se pudo confirmar el pago"}
            </h1>
            <p className="font-body text-ink/60 mt-2">
              {state === "timeout"
                ? "Puede tardar unos instantes. Revisa tu membresía en unos minutos."
                : "Si el problema persiste, inténtalo de nuevo."}
            </p>
            <Link to="/membresia" className="inline-block mt-6 border border-plum/30 text-plum font-sans px-7 py-3 rounded-full hover:bg-plum/5 transition-colors">
              Volver a membresía
            </Link>
          </>
        )}
      </div>
    </div>
  );
}
